import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import salesRadarService from '../../services/salesRadarService';

// Default stage colors
const STAGE_COLORS = [
  '#6B7280', // Gray
  '#2563EB', // Blue
  '#7C3AED', // Purple
  '#F59E0B', // Amber
  '#DC2626', // Red
  '#16A34A', // Green
  '#0891B2', // Cyan
  '#DB2777', // Pink
];

// Default stages
const DEFAULT_STAGES = [
  { id: 'new', name: 'New Lead', color: '#6B7280', order: 0, description: 'Newly identified leads', auto_advance_score: null, is_default: true },
  { id: 'contacted', name: 'Contacted', color: '#2563EB', order: 1, description: 'Initial outreach made', auto_advance_score: 30, is_default: false },
  { id: 'qualified', name: 'Qualified', color: '#7C3AED', order: 2, description: 'Lead meets qualification criteria', auto_advance_score: 50, is_default: false },
  { id: 'proposal', name: 'Proposal', color: '#F59E0B', order: 3, description: 'Proposal sent to lead', auto_advance_score: 70, is_default: false },
  { id: 'negotiation', name: 'Negotiation', color: '#DC2626', order: 4, description: 'In active negotiation', auto_advance_score: 85, is_default: false },
  { id: 'won', name: 'Won', color: '#16A34A', order: 5, description: 'Deal closed successfully', auto_advance_score: null, is_default: false },
  { id: 'lost', name: 'Lost', color: '#374151', order: 6, description: 'Deal lost or lead disqualified', auto_advance_score: null, is_default: false },
];

function StageManagement() {
  const navigate = useNavigate();

  // State
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: STAGE_COLORS[0],
    description: '',
    auto_advance_score: null,
    is_default: false,
  });
  const [notification, setNotification] = useState(null);
  const [draggedStage, setDraggedStage] = useState(null);

  // Fetch stages
  const fetchStages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await salesRadarService.getStages();
      setStages(response.data || []);
    } catch (err) {
      console.error(err);
      // Use default stages
      setStages(DEFAULT_STAGES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  // Handle dialog
  const handleOpenDialog = (stage = null) => {
    if (stage) {
      setEditingStage(stage);
      setFormData({
        name: stage.name,
        color: stage.color,
        description: stage.description || '',
        auto_advance_score: stage.auto_advance_score,
        is_default: stage.is_default || false,
      });
    } else {
      setEditingStage(null);
      setFormData({
        name: '',
        color: STAGE_COLORS[stages.length % STAGE_COLORS.length],
        description: '',
        auto_advance_score: null,
        is_default: false,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingStage(null);
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Save stage
  const handleSaveStage = async () => {
    try {
      if (editingStage) {
        await salesRadarService.updateStage(editingStage.id, formData);
        setStages(prev => prev.map(s => s.id === editingStage.id ? { ...s, ...formData } : s));
        setNotification({ type: 'success', message: 'Stage updated successfully' });
      } else {
        const newStage = {
          ...formData,
          id: `stage_${Date.now()}`,
          order: stages.length,
        };
        await salesRadarService.createStage(newStage);
        setStages(prev => [...prev, newStage]);
        setNotification({ type: 'success', message: 'Stage created successfully' });
      }
      handleCloseDialog();
    } catch (err) {
      // Update local state anyway for demo
      if (editingStage) {
        setStages(prev => prev.map(s => s.id === editingStage.id ? { ...s, ...formData } : s));
      } else {
        setStages(prev => [...prev, { ...formData, id: `stage_${Date.now()}`, order: prev.length }]);
      }
      handleCloseDialog();
      setNotification({ type: 'success', message: editingStage ? 'Stage updated' : 'Stage created' });
    }
    setTimeout(() => setNotification(null), 3000);
  };

  // Delete stage
  const handleDeleteStage = async (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    if (stage?.is_default) {
      setNotification({ type: 'error', message: 'Cannot delete the default stage' });
      setTimeout(() => setNotification(null), 3000);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this stage? Leads in this stage will be moved to the default stage.')) {
      return;
    }

    try {
      await salesRadarService.deleteStage(stageId);
    } catch (err) {
      // Continue anyway for demo
    }
    setStages(prev => prev.filter(s => s.id !== stageId));
    setNotification({ type: 'success', message: 'Stage deleted successfully' });
    setTimeout(() => setNotification(null), 3000);
  };

  // Set default stage
  const handleSetDefault = async (stageId) => {
    try {
      await salesRadarService.setDefaultStage(stageId);
    } catch (err) {
      // Continue anyway for demo
    }
    setStages(prev => prev.map(s => ({ ...s, is_default: s.id === stageId })));
    setNotification({ type: 'success', message: 'Default stage updated' });
    setTimeout(() => setNotification(null), 3000);
  };

  // Drag and drop handlers
  const handleDragStart = (stage) => {
    setDraggedStage(stage);
  };

  const handleDragOver = (e, targetStage) => {
    e.preventDefault();
    if (!draggedStage || draggedStage.id === targetStage.id) return;

    const newStages = [...stages];
    const draggedIndex = newStages.findIndex(s => s.id === draggedStage.id);
    const targetIndex = newStages.findIndex(s => s.id === targetStage.id);

    newStages.splice(draggedIndex, 1);
    newStages.splice(targetIndex, 0, draggedStage);

    // Update order
    newStages.forEach((s, i) => { s.order = i; });
    setStages(newStages);
  };

  const handleDragEnd = async () => {
    if (!draggedStage) return;

    try {
      const updates = stages.map((s, i) => ({ id: s.id, order: i }));
      await salesRadarService.reorderStages(updates);
    } catch (err) {
      console.error(err);
    }
    setDraggedStage(null);
  };

  // Get stage stats
  const getStageStats = (stageId) => {
    // Mock stats for demo
    const mockLeadCounts = {
      new: 45,
      contacted: 32,
      qualified: 28,
      proposal: 15,
      negotiation: 8,
      won: 12,
      lost: 18,
    };
    return {
      leadCount: mockLeadCounts[stageId] || Math.floor(Math.random() * 50),
      avgDaysInStage: Math.floor(Math.random() * 14) + 1,
    };
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav style={{
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        color: 'var(--gray-600)',
        fontSize: '0.875rem'
      }}>
        <button onClick={() => navigate('/')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Home
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <button onClick={() => navigate('/sales-radar')} className="btn btn-link" style={{ padding: 0, color: 'var(--primary-600)' }}>
          Sales Radar
        </button>
        <span style={{ margin: '0 0.5rem', color: 'var(--gray-400)' }}>/</span>
        <span style={{ color: 'var(--gray-600)' }}>Stage Management</span>
      </nav>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          background: notification.type === 'success' ? '#DCFCE7' : '#FEE2E2',
          color: notification.type === 'success' ? '#16A34A' : '#DC2626',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
        }}>
          {notification.message}
        </div>
      )}

      {/* Header Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #7C3AED'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              📊 Stage Management
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Configure your sales pipeline stages and their progression rules
            </p>
          </div>
          <button onClick={() => handleOpenDialog()} className="btn btn-primary">
            ➕ Add Stage
          </button>
        </div>

        {/* Pipeline Visualization */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: 'var(--gray-700)' }}>
            Pipeline Flow
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            overflowX: 'auto',
            padding: '1rem 0',
            gap: '0.5rem',
          }}>
            {stages.sort((a, b) => a.order - b.order).map((stage, index) => (
              <React.Fragment key={stage.id}>
                <div style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  background: stage.color,
                  color: 'white',
                  fontWeight: '500',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                }}>
                  {stage.name}
                  {stage.is_default && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>⭐</span>
                  )}
                </div>
                {index < stages.length - 1 && (
                  <span style={{ color: 'var(--gray-400)', fontSize: '1.5rem' }}>→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Stages List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div style={{ color: 'var(--gray-500)' }}>Loading stages...</div>
        </div>
      ) : (
        <div className="card">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.75rem 1rem',
            background: 'var(--gray-50)',
            borderBottom: '1px solid var(--gray-200)',
            fontWeight: '600',
            fontSize: '0.875rem',
            color: 'var(--gray-600)',
          }}>
            <div style={{ width: '40px' }}></div>
            <div style={{ width: '200px' }}>Stage</div>
            <div style={{ width: '150px' }}>Color</div>
            <div style={{ flex: 1 }}>Description</div>
            <div style={{ width: '120px', textAlign: 'center' }}>Auto-Advance</div>
            <div style={{ width: '100px', textAlign: 'center' }}>Leads</div>
            <div style={{ width: '120px', textAlign: 'center' }}>Avg. Days</div>
            <div style={{ width: '150px', textAlign: 'right' }}>Actions</div>
          </div>

          {stages.sort((a, b) => a.order - b.order).map((stage) => {
            const stats = getStageStats(stage.id);
            return (
              <div
                key={stage.id}
                draggable
                onDragStart={() => handleDragStart(stage)}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDragEnd={handleDragEnd}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '1rem',
                  borderBottom: '1px solid var(--gray-100)',
                  background: draggedStage?.id === stage.id ? 'var(--gray-50)' : 'white',
                  cursor: 'grab',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
                onMouseLeave={(e) => e.currentTarget.style.background = draggedStage?.id === stage.id ? 'var(--gray-50)' : 'white'}
              >
                <div style={{ width: '40px', color: 'var(--gray-400)' }}>⋮⋮</div>
                <div style={{ width: '200px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{stage.name}</span>
                  {stage.is_default && (
                    <span style={{
                      padding: '0.125rem 0.5rem',
                      borderRadius: '9999px',
                      fontSize: '0.625rem',
                      background: '#FEF3C7',
                      color: '#D97706',
                    }}>
                      Default
                    </span>
                  )}
                </div>
                <div style={{ width: '150px' }}>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '6px',
                    background: stage.color,
                    color: 'white',
                    fontSize: '0.75rem',
                  }}>
                    <span style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: 'white',
                      opacity: 0.3,
                    }} />
                    {stage.color}
                  </div>
                </div>
                <div style={{ flex: 1, fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                  {stage.description || '-'}
                </div>
                <div style={{ width: '120px', textAlign: 'center' }}>
                  {stage.auto_advance_score ? (
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      background: '#DBEAFE',
                      color: '#2563EB',
                      fontSize: '0.75rem',
                    }}>
                      Score ≥ {stage.auto_advance_score}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--gray-400)', fontSize: '0.875rem' }}>Manual</span>
                  )}
                </div>
                <div style={{ width: '100px', textAlign: 'center', fontWeight: '500' }}>
                  {stats.leadCount}
                </div>
                <div style={{ width: '120px', textAlign: 'center', color: 'var(--gray-600)' }}>
                  {stats.avgDaysInStage} days
                </div>
                <div style={{ width: '150px', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  {!stage.is_default && (
                    <button
                      onClick={() => handleSetDefault(stage.id)}
                      title="Set as default"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      ⭐
                    </button>
                  )}
                  <button
                    onClick={() => handleOpenDialog(stage)}
                    title="Edit"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                  >
                    ✏️
                  </button>
                  {!stage.is_default && (
                    <button
                      onClick={() => handleDeleteStage(stage.id)}
                      title="Delete"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem' }}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stage Dialog */}
      {dialogOpen && (
        <>
          <div
            onClick={handleCloseDialog}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            width: '450px',
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'var(--gray-900)' }}>
              {editingStage ? 'Edit Stage' : 'Create New Stage'}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Stage Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder="e.g., Qualified Lead"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Color
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {STAGE_COLORS.map(color => (
                    <button
                      key={color}
                      onClick={() => handleFormChange('color', color)}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: color,
                        border: formData.color === color ? '3px solid var(--gray-900)' : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'transform 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  className="form-textarea"
                  style={{ width: '100%', minHeight: '80px' }}
                  placeholder="Describe this stage"
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Auto-Advance at Score
                </label>
                <input
                  type="number"
                  value={formData.auto_advance_score || ''}
                  onChange={(e) => handleFormChange('auto_advance_score', e.target.value ? parseInt(e.target.value) : null)}
                  className="form-input"
                  style={{ width: '100%' }}
                  placeholder="Leave empty for manual only"
                  min="0"
                  max="100"
                />
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  Leads will automatically advance to this stage when their score reaches this threshold
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button onClick={handleCloseDialog} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSaveStage}
                className="btn btn-primary"
                disabled={!formData.name}
              >
                {editingStage ? 'Update Stage' : 'Create Stage'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StageManagement;
